import React, { useMemo, useEffect, useRef } from 'react';
import { Select, SIZE, TYPE } from 'baseui/select';
import {
  LIBRARY_SORT_BY_READS,
  LIBRARY_SORT_DEFAULT,
} from '../utils/readHistoryCounts';
import './styles/QuestionnaireFilters.css';

const LIBRARY_SORT_OPTIONS = [
  { id: LIBRARY_SORT_DEFAULT, label: 'Default order', value: LIBRARY_SORT_DEFAULT },
  { id: LIBRARY_SORT_BY_READS, label: 'Most reads', value: LIBRARY_SORT_BY_READS },
];

export function filterSelectOptions(options, filterValue, excludeOptions, newProps = {}) {
  const valueKey = newProps?.valueKey ?? 'value';
  const labelKey = newProps?.labelKey ?? 'label';

  const excludeValues = (excludeOptions || []).reduce((acc, option) => {
    if (option != null && option[valueKey] != null) {
      acc.add(option[valueKey]);
    }
    return acc;
  }, new Set());

  let normalized = String(filterValue ?? '');
  if (newProps?.trimFilter !== false) {
    normalized = normalized.trim();
  }
  if (newProps?.ignoreCase !== false) {
    normalized = normalized.toLowerCase();
  }

  return options.filter((opt) => {
    if (excludeValues.has(opt[valueKey])) return false;
    if (!normalized) return true;

    const id = String(opt.id ?? '').toLowerCase();
    const value = String(opt.value ?? opt.id ?? '').toLowerCase();
    const label = String(opt[labelKey] ?? opt.label ?? opt.id ?? '').toLowerCase();
    const category = String(opt.category ?? '').toLowerCase();
    return (
      id.includes(normalized) ||
      value.includes(normalized) ||
      label.includes(normalized) ||
      category.includes(normalized)
    );
  });
}

function renderSuggestionLabel(label) {
  if (!label || typeof label !== 'string') return label ?? '';
  const sep = label.indexOf(': ');
  if (sep <= 0) return label;
  const key = label.slice(0, sep + 1);
  const val = label.slice(sep + 2);
  return (
    <span className="search-suggestion-row">
      <span className="search-suggestion-key">{key}</span>{' '}
      <span className="search-suggestion-value">{val}</span>
    </span>
  );
}

function formatSuggestionLabel(category, text) {
  const t = text?.trim();
  if (!t) return '';
  const snippet = t.length > 72 ? `${t.slice(0, 69)}…` : t;
  return `${category}: ${snippet}`;
}

function buildSearchSuggestions(query, data) {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const out = [];
  const seen = new Set();

  const push = (text, category) => {
    const t = text?.trim();
    if (!t) return;
    const key = `${category}:${t.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      id: `${category}-${t.slice(0, 48)}-${out.length}`,
      label: formatSuggestionLabel(category, t),
      value: t,
      category,
    });
  };

  for (const item of data) {
    if (String(item.id).includes(q)) push(String(item.id), 'ID');
    if (item.question?.toLowerCase().includes(q)) push(item.question, 'Question');
    if (item.subCategory2?.toLowerCase().includes(q)) push(item.subCategory2, 'Topic');
    if (item.topic?.toLowerCase().includes(q)) push(item.topic, 'Notebook');
    if (item.subCategory1?.toLowerCase().includes(q)) push(item.subCategory1, 'Level');
    if (item.answer?.toLowerCase().includes(q)) {
      const idx = item.answer.toLowerCase().indexOf(q);
      const start = Math.max(0, idx - 24);
      push(item.answer.slice(start, start + 72), 'Answer');
    }
    if (out.length >= 16) break;
  }

  return out;
}

const selectRootOverrides = {
  Root: { style: { width: '100%', minWidth: 0 } },
  ControlContainer: { style: { width: '100%' } },
  Popover: {
    props: {
      overrides: {
        Body: { style: { maxHeight: '280px' } },
      },
    },
  },
};

const QuestionnaireFilters = ({
  searchQuery,
  onSearchInputChange,
  onSearchOptionPick,
  onSearchClear,
  searchLoading,
  autoFocusSearch,
  filterData,
  topics,
  selectedNoteBook,
  selectedTopic,
  selectedLevels,
  selectedArchivalDate,
  selectorLoading,
  onNotebookChange,
  onTopicChange,
  onLevelChange,
  onArchivalChange,
  transformOptions,
  originalQuestionData,
  librarySort = LIBRARY_SORT_DEFAULT,
  onLibrarySortChange,
}) => {
  const searchControlRef = useRef(null);

  useEffect(() => {
    searchControlRef.current?.setInputValue(searchQuery ?? '');
  }, [searchQuery]);

  const notebookOptions = useMemo(
    () => transformOptions(filterData.topic || []),
    [filterData.topic, transformOptions]
  );
  const levelOptions = useMemo(
    () => transformOptions(filterData.subCategory1 || []),
    [filterData.subCategory1, transformOptions]
  );
  const archivalOptions = useMemo(
    () => transformOptions(filterData.archiveFilter || []),
    [filterData.archiveFilter, transformOptions]
  );

  const searchOptions = useMemo(() => {
    const typed = buildSearchSuggestions(searchQuery, originalQuestionData || []);
    if (typed.length > 0) return typed;

    const q = searchQuery.trim().toLowerCase();
    if (q.length > 0) return [];

    const hints = [];
    const seen = new Set();
    const addHint = (text, category) => {
      const t = text?.trim();
      if (!t || seen.has(`${category}:${t.toLowerCase()}`)) return;
      seen.add(`${category}:${t.toLowerCase()}`);
      hints.push({
        id: `hint-${category}-${t}`,
        label: formatSuggestionLabel(category, t),
        value: t,
        category,
      });
    };

    (filterData.topic || []).slice(0, 4).forEach((t) => addHint(t, 'Notebook'));
    (filterData.subCategory1 || []).slice(0, 3).forEach((t) => addHint(t, 'Level'));
    (originalQuestionData || []).slice(0, 6).forEach((item) => {
      if (item.subCategory2) addHint(item.subCategory2, 'Topic');
    });

    return hints.slice(0, 10);
  }, [searchQuery, originalQuestionData, filterData]);

  const searchValue = useMemo(() => {
    if (!searchQuery?.trim()) return [];
    const match = searchOptions.find(
      (o) => o.value.toLowerCase() === searchQuery.trim().toLowerCase()
    );
    return match ? [match] : [];
  }, [searchQuery, searchOptions]);

  const librarySortValue = useMemo(() => {
    const match = LIBRARY_SORT_OPTIONS.find((o) => o.value === librarySort);
    return match ? [match] : [LIBRARY_SORT_OPTIONS[0]];
  }, [librarySort]);

  return (
    <div className="questionnaire-filter-bar filter-select-overrides">
      <div className="filter-field filter-search">
        <Select
          type={TYPE.search}
          size={SIZE.compact}
          placeholder="Search keyword"
          isLoading={searchLoading}
          options={searchOptions}
          value={searchValue}
          labelKey="label"
          valueKey="id"
          controlRef={searchControlRef}
          onInputChange={(e) => onSearchInputChange(e.currentTarget.value)}
          onChange={({ value, option }) => {
            if (option) {
              onSearchOptionPick(option.value ?? option.id ?? option.label);
            } else if (value?.[0]) {
              onSearchOptionPick(value[0].value ?? value[0].id);
            }
          }}
          clearable
          onClear={onSearchClear}
          onSelectResetsInput={false}
          onBlurResetsInput={false}
          openOnClick
          filterOptions={filterSelectOptions}
          getOptionLabel={({ option }) => {
            if (!option) return '';
            let text = option.label;
            if (!text && option.category && option.value) {
              text = formatSuggestionLabel(option.category, option.value);
            }
            if (!text) return option.id ?? '';
            return renderSuggestionLabel(text);
          }}
          overrides={{
            ...selectRootOverrides,
            Input: {
              props: {
                autoFocus: autoFocusSearch,
              },
            },
          }}
        />
      </div>

      <div className="filter-field filter-notebook">
        <Select
          multi
          type={TYPE.select}
          labelKey="id"
          valueKey="value"
          size={SIZE.compact}
          value={selectedNoteBook}
          options={notebookOptions}
          placeholder="Notebook"
          isLoading={selectorLoading}
          onChange={({ value }) => onNotebookChange(value)}
          filterOptions={filterSelectOptions}
          overrides={selectRootOverrides}
        />
      </div>

      <div className="filter-field filter-topic">
        <Select
          multi
          type={TYPE.select}
          labelKey="id"
          valueKey="value"
          size={SIZE.compact}
          value={selectedTopic}
          options={topics || []}
          placeholder="Topic"
          isLoading={selectorLoading}
          onChange={({ value }) => onTopicChange(value)}
          filterOptions={filterSelectOptions}
          overrides={selectRootOverrides}
        />
      </div>

      <div className="filter-field filter-level">
        <Select
          multi
          type={TYPE.select}
          labelKey="id"
          valueKey="value"
          size={SIZE.compact}
          value={selectedLevels}
          options={levelOptions}
          placeholder="Level"
          isLoading={selectorLoading}
          onChange={({ value }) => onLevelChange(value)}
          filterOptions={filterSelectOptions}
          overrides={selectRootOverrides}
        />
      </div>

      <div className="filter-field filter-archival">
        <Select
          type={TYPE.select}
          labelKey="id"
          valueKey="value"
          size={SIZE.compact}
          value={selectedArchivalDate}
          options={archivalOptions}
          placeholder="Archival"
          isLoading={selectorLoading}
          onChange={({ value }) => onArchivalChange(value)}
          filterOptions={filterSelectOptions}
          overrides={selectRootOverrides}
        />
      </div>

      <div className="filter-field filter-sort">
        <Select
          type={TYPE.select}
          labelKey="label"
          valueKey="value"
          size={SIZE.compact}
          value={librarySortValue}
          options={LIBRARY_SORT_OPTIONS}
          placeholder="Sort"
          onChange={({ value }) => {
            const next = value?.[0]?.value ?? LIBRARY_SORT_DEFAULT;
            onLibrarySortChange?.(next);
          }}
          clearable={false}
          searchable={false}
          overrides={selectRootOverrides}
        />
      </div>
    </div>
  );
};

export default QuestionnaireFilters;
